import { useEffect, useRef } from "react";
import { Capacitor, registerPlugin } from "@capacitor/core";
import { supabase } from "./supabase";

// useActivityPushTokens — captures both flavors of ActivityKit push
// token and persists them to Supabase so the cook-activity-push edge
// function can deliver lock-screen updates without the participant's
// app being awake.
//
// TWO TOKEN STREAMS:
//
//   1. pushToStartToken (iOS 17.2+)
//      Process-wide token. Stored once per user in
//      `cook_pushtostart_tokens`. The server uses it to SPAWN a
//      Live Activity remotely the moment a cook starts — without
//      Marissa or Paul ever opening the app.
//
//   2. activityToken (every iOS 16.1+ Live Activity)
//      One per running activity. Stored per (user, cook_session) in
//      `cook_activity_push_tokens`. The server uses these to deliver
//      ContentState UPDATES as the lead advances steps.
//
// LIFECYCLE:
//   - Mount in App after auth resolves (iOS only).
//   - On mount: ask the plugin for the cached push-to-start token
//     (the system may have delivered it before listeners were ready).
//   - Subscribe to "pushToStartTokenUpdate" + "activityTokenUpdate"
//     events for future rotations.
//   - On every token: upsert to Supabase. Fire-and-forget; failures
//     log but don't break.
//
// CLEANUP: when an activity ends (BoodaTimer.endSession), we don't
// proactively delete the activity token row — the database trigger
// for cook_sessions.status='finished'|'abandoned' fires an APNs
// 'end' event that clears the tokens server-side. v2 can add explicit
// client-side cleanup if egress on those rows starts mattering.

const BoodaTimer = registerPlugin("BoodaTimer");

const IS_IOS_NATIVE =
  typeof window !== "undefined"
  && Capacitor.isNativePlatform?.()
  && Capacitor.getPlatform?.() === "ios";

// clientId shape is `cook-${cookSessionId}` — both useTeamLiveActivity
// and CookMode use that exact prefix when calling BoodaTimer. Parsing
// it here lets the persistence layer map clientId → session_id
// without an extra round-trip.
function sessionIdFromClientId(clientId) {
  if (typeof clientId !== "string") return null;
  const m = clientId.match(/^cook-(.+)$/);
  return m ? m[1] : null;
}

async function persistPushToStartToken(userId, token) {
  if (!userId || !token) return;
  const { error } = await supabase
    .from("cook_pushtostart_tokens")
    .upsert(
      {
        user_id:    userId,
        token,
        platform:   "ios",
        updated_at: new Date().toISOString(),
      },
      { onConflict: "user_id" },
    );
  if (error) {
    console.warn("[activityPushTokens] pushToStart upsert failed:", error.message);
  } else {
    console.info("[activityPushTokens] pushToStart token persisted");
  }
}

async function persistActivityToken(userId, clientId, token) {
  if (!userId || !token) return;
  const sessionId = sessionIdFromClientId(clientId);
  if (!sessionId) {
    console.warn("[activityPushTokens] clientId didn't match cook-<uuid>", clientId);
    return;
  }
  const { error } = await supabase
    .from("cook_activity_push_tokens")
    .upsert(
      {
        user_id:         userId,
        cook_session_id: sessionId,
        token,
        platform:        "ios",
        last_seen_at:    new Date().toISOString(),
      },
      { onConflict: "user_id,cook_session_id" },
    );
  if (error) {
    console.warn("[activityPushTokens] activity upsert failed:", error.message);
  } else {
    console.info("[activityPushTokens] activity token persisted", { sessionId });
  }
}

export function useActivityPushTokens(userId) {
  // Hold last-persisted hex per stream so we don't re-write on every
  // duplicate event. ActivityKit can fire the same token several
  // times in quick succession on cold start.
  const lastPushToStartRef = useRef(null);
  const lastActivityTokensRef = useRef(new Map()); // clientId → hex

  useEffect(() => {
    console.info("[activityPushTokens] hook mount", { userId, isIOS: IS_IOS_NATIVE });
    if (!IS_IOS_NATIVE || !userId) return undefined;

    let alive = true;

    // Ask for any cached token the plugin already has from before
    // listeners were registered. The plugin's load() hook also
    // re-emits cached tokens when listeners attach, so this is a
    // belt-and-suspenders read.
    (async () => {
      try {
        const { token } = await BoodaTimer.getPushToStartToken();
        if (!alive) return;
        if (typeof token === "string" && token.length > 0
            && lastPushToStartRef.current !== token) {
          lastPushToStartRef.current = token;
          persistPushToStartToken(userId, token);
        }
      } catch (e) {
        console.warn("[activityPushTokens] getPushToStartToken failed:", e?.message || e);
      }
    })();

    const pushToStartListener = BoodaTimer.addListener(
      "pushToStartTokenUpdate",
      ({ token }) => {
        if (!alive) return;
        if (typeof token !== "string" || token.length === 0) return;
        if (lastPushToStartRef.current === token) return;
        lastPushToStartRef.current = token;
        persistPushToStartToken(userId, token);
      },
    );

    const activityListener = BoodaTimer.addListener(
      "activityTokenUpdate",
      ({ clientId, token }) => {
        console.info("[activityPushTokens] activityTokenUpdate event", {
          clientId,
          tokenLen: typeof token === "string" ? token.length : "n/a",
          alive,
        });
        if (!alive) return;
        if (typeof token !== "string" || token.length === 0) return;
        if (typeof clientId !== "string" || clientId.length === 0) return;
        if (lastActivityTokensRef.current.get(clientId) === token) return;
        lastActivityTokensRef.current.set(clientId, token);
        persistActivityToken(userId, clientId, token);
      },
    );

    return () => {
      alive = false;
      pushToStartListener?.then?.((l) => l?.remove?.()).catch?.(() => null);
      activityListener?.then?.((l) => l?.remove?.()).catch?.(() => null);
    };
  }, [userId]);
}
